import { html, css, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { nsLayoutStyles } from '../../../../../ns-styles/index.js';

import { callLambda } from '../../../../../helpers/lambda.js';
import { renderFormErrorSpan } from '../../../../../helpers/dom.js';
import { FinalFormController, FormConfig } from '../../../../../ns-form/final-form-controller.js';

import NSJobComponent from '../../../job.js';

import Company from '../../../../../models/company.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '../../../../../ns-elements/ns-job-layout.js';
import '../../../../../ns-elements/ns-detail-list.js';
import type { DetailRow } from '../../../../../ns-elements/ns-detail-list.js';

export interface BankFormField<FormValues> {
  name: keyof FormValues & string;
  label: string;
  type?: string;
  requiredMessage?: string;
}

export interface BankFormInfo {
  label: string;
  text?: string;
  steps?: string[];
}

// Read-only values the person needs before filling in the form, such as the account a transfer goes to
export interface BankFormDetails {
  label: string;
  rows: DetailRow[];
  // Shown instead of the rows when they couldn't be loaded
  unavailable?: string;
}

export interface BankFormCopy<FormValues> {
  intro: string;
  heading: string;
  details?: BankFormDetails;
  fields: BankFormField<FormValues>[];
  lambda: string;
  loadingMessage: string;
  // Sections of the info panel, after the details
  info?: BankFormInfo[];
}

export default abstract class NSBankFormJob<FormValues extends object> extends NSJobComponent {
  protected _company?: Company;

  protected _form: FinalFormController<FormValues>;

  private __errors: Partial<Record<keyof FormValues, string>> = {};

  static styles = [
    nsLayoutStyles,
    ...super.styles,
    css`
      .field + .field {
        margin-top: var(--wa-space-m);
      }

      .primary-info ol,
      .primary-info ns-detail-list {
        margin: 0 0 var(--wa-space-m);
      }
      .primary-info ol {
        padding-inline-start: var(--wa-space-l);
        font-weight: 300;
      }
      .primary-info li + li {
        margin-top: var(--wa-space-2xs);
      }

      .primary-info ns-detail-list {
        --ns-detail-list-gap: var(--wa-space-2xs);
      }

      .primary-info ns-detail-list::part(value) {
        font-size: var(--wa-font-size-m);
      }
    `,
  ];

  protected abstract get _copy(): BankFormCopy<FormValues>;

  protected abstract _requestBody(values: Partial<FormValues>): Record<string, unknown>;

  constructor() {
    super();

    this._form = new FinalFormController(this, {
      onSubmit: async (values) => await this.__submit(values),
      validate: () => ({}),
    } as FormConfig<FormValues>);
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this.updateComplete;

    if (!this._db) throw new Error('Unable to find db service');

    if (!this._directory) throw new Error('Unable to find NSDirectoryService');

    if (!this._auth) throw new Error('Unable to find auth service');

    if (!this._job) throw new Error('No job available');

    await this._directory.initialised;

    this._company = await this.loadJobCompany();
  }

  private async __submit(values: Partial<FormValues>) {
    if (!this._job) throw new Error('Missing required job');
    this.clearError();

    const { fields, lambda, loadingMessage } = this._copy;
    this.__errors = {};
    fields.forEach((field) => {
      if (field.requiredMessage && !values[field.name]) this.__errors[field.name] = field.requiredMessage;
    });
    if (Object.keys(this.__errors).length > 0) return;

    try {
      this.loading(loadingMessage);

      if (!this._auth) throw new Error('Missing auth service');
      if (!this._company) throw new Error('Missing nodeStream company data');
      if (!this._job) throw new Error('Missing job info');

      await callLambda(
        lambda,
        'POST',
        this._auth.token,
        JSON.stringify({ companyId: this._company.id, jobId: this._job.id, ...this._requestBody(values) }),
      );

      this.loaded();
      this.outcome('complete');
    } catch (err: unknown) {
      this.loaded();
      this.showError(err);
    }
  }

  render() {
    const { form, register } = this._form;
    const { intro, heading, details, fields, info } = this._copy;

    return html`
      <ns-job-layout>
        <p slot="intro">${intro}</p>
        <div class="job-panel">
          <h3>${heading}</h3>
          <form
            id="bank-details-form"
            @submit=${(e: Event) => {
              e.preventDefault();
              form.submit();
            }}
          >
            ${fields.map(
              (field) => html`
                <div class="field layout vertical">
                  <ns-input-field
                    stacked
                    ?required=${!!field.requiredMessage}
                    ${register(field.name)}
                    type=${ifDefined(field.type)}
                    placeholder="Enter Here"
                    label="${field.label}${field.requiredMessage ? '*' : ''}"
                  ></ns-input-field>
                  ${renderFormErrorSpan(field.name, this.__errors, form)}
                </div>
              `,
            )}
          </form>
        </div>
        ${details || info
          ? html`
              <div slot="aside" class="primary-info">
                <span class="info-icon" aria-hidden="true">i</span>
                ${details
                  ? html`
                      <h3>${details.label}</h3>
                      ${details.unavailable
                        ? html`
                            <p>${details.unavailable}</p>
                          `
                        : html`
                            <ns-detail-list .rows=${details.rows}></ns-detail-list>
                          `}
                    `
                  : nothing}
                ${info?.map(
                  (section) => html`
                    <h3>${section.label}</h3>
                    ${section.text
                      ? html`
                          <p>${section.text}</p>
                        `
                      : nothing}
                    ${section.steps
                      ? html`
                          <ol>
                            ${section.steps.map(
                              (step) => html`
                                <li>${step}</li>
                              `,
                            )}
                          </ol>
                        `
                      : nothing}
                  `,
                )}
              </div>
            `
          : nothing}
      </ns-job-layout>
    `;
  }

  renderActions() {
    const { form } = this._form;
    return html`
      <wa-button variant="brand" type="button" ?disabled=${form.getState().submitting} @click=${() => form.submit()}>
        Complete
      </wa-button>
    `;
  }
}
